import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Plus, Pencil, Trash, Bank, CurrencyBtc } from '@phosphor-icons/react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const emptyBank = { bank_name: '', beneficiary: '', iban: '', swift: '', account_number: '',
  bank_address: '', beneficiary_address: '', reference: '', currency: 'USD', min_deposit: 0,
  instructions: '', enabled: true, display_order: 0 };
const emptyWallet = { coin: '', network: '', address: '', memo: '', min_deposit: 0,
  instructions: '', enabled: true, display_order: 0 };

const BooleanPill = ({ v }) => (
  <span className={`px-2 py-1 rounded text-xs font-semibold ${v ? 'bg-emerald-100 text-emerald-800' : 'bg-slate-200 text-slate-600'}`}>
    {v ? 'Visible' : 'Hidden'}
  </span>
);

const AdminPaymentSettings = () => {
  const [banks, setBanks] = useState([]);
  const [wallets, setWallets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingBank, setEditingBank] = useState(null);   // null | 'new' | object
  const [editingWallet, setEditingWallet] = useState(null);
  const [bankForm, setBankForm] = useState(emptyBank);
  const [walletForm, setWalletForm] = useState(emptyWallet);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    try {
      const [b, w] = await Promise.all([
        axios.get(`${API}/admin/payment-methods/banks`),
        axios.get(`${API}/admin/payment-methods/crypto`),
      ]);
      setBanks(b.data); setWallets(w.data);
    } catch (e) { toast.error('Failed to load payment settings'); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const openBank = (b) => { setBankForm(b ? { ...emptyBank, ...b } : emptyBank); setEditingBank(b || 'new'); };
  const openWallet = (w) => { setWalletForm(w ? { ...emptyWallet, ...w } : emptyWallet); setEditingWallet(w || 'new'); };

  const saveBank = async () => {
    setSaving(true);
    try {
      if (editingBank === 'new') await axios.post(`${API}/admin/payment-methods/banks`, bankForm);
      else await axios.put(`${API}/admin/payment-methods/banks/${editingBank.id}`, bankForm);
      toast.success('Bank account saved — clients see the change immediately');
      setEditingBank(null); load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Save failed'); }
    finally { setSaving(false); }
  };
  const saveWallet = async () => {
    setSaving(true);
    try {
      if (editingWallet === 'new') await axios.post(`${API}/admin/payment-methods/crypto`, walletForm);
      else await axios.put(`${API}/admin/payment-methods/crypto/${editingWallet.id}`, walletForm);
      toast.success('Crypto wallet saved — clients see the change immediately');
      setEditingWallet(null); load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Save failed'); }
    finally { setSaving(false); }
  };
  const deleteBank = async (id) => {
    if (!window.confirm('Delete this bank account? This cannot be undone.')) return;
    try { await axios.delete(`${API}/admin/payment-methods/banks/${id}`); toast.success('Deleted'); load(); }
    catch (e) { toast.error('Delete failed'); }
  };
  const deleteWallet = async (id) => {
    if (!window.confirm('Delete this crypto wallet? This cannot be undone.')) return;
    try { await axios.delete(`${API}/admin/payment-methods/crypto/${id}`); toast.success('Deleted'); load(); }
    catch (e) { toast.error('Delete failed'); }
  };
  const toggleBank = async (b) => {
    try { await axios.put(`${API}/admin/payment-methods/banks/${b.id}`, { enabled: !b.enabled }); load(); }
    catch (e) { toast.error('Toggle failed'); }
  };
  const toggleWallet = async (w) => {
    try { await axios.put(`${API}/admin/payment-methods/crypto/${w.id}`, { enabled: !w.enabled }); load(); }
    catch (e) { toast.error('Toggle failed'); }
  };

  return (
    <div className="p-8" data-testid="admin-payment-settings-page">
      <div className="mb-6">
        <h1 className="text-3xl font-bold text-brand-primary">Payments / Funding Settings</h1>
        <p className="text-slate-600 mt-1">
          Manage the bank accounts and crypto wallets clients see when they Deposit or Withdraw. Changes go live immediately — no redeploy needed.
        </p>
      </div>

      <Tabs defaultValue="banks">
        <TabsList>
          <TabsTrigger value="banks" data-testid="tab-banks"><Bank size={16} className="mr-2" />Bank Accounts</TabsTrigger>
          <TabsTrigger value="crypto" data-testid="tab-crypto"><CurrencyBtc size={16} className="mr-2" />Crypto Wallets</TabsTrigger>
        </TabsList>

        <TabsContent value="banks" className="mt-4">
          <div className="flex justify-end mb-3">
            <Button onClick={() => openBank(null)} data-testid="add-bank-btn"><Plus size={16} className="mr-1" />Add Bank Account</Button>
          </div>
          <div className="bg-white rounded-xl border border-slate-200 overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 uppercase text-xs tracking-wider text-slate-600">
                <tr>
                  <th className="p-3 text-left">Bank</th><th className="p-3 text-left">Beneficiary</th>
                  <th className="p-3 text-left">IBAN / Account</th><th className="p-3 text-left">SWIFT</th>
                  <th className="p-3 text-left">Currency</th><th className="p-3 text-right">Min</th>
                  <th className="p-3">Status</th><th className="p-3">Actions</th>
                </tr>
              </thead>
              <tbody>
                {loading ? <tr><td colSpan="8" className="p-6 text-center text-slate-500">Loading…</td></tr>
                : banks.length === 0 ? <tr><td colSpan="8" className="p-6 text-center text-slate-500">No bank accounts configured yet.</td></tr>
                : banks.map(b => (
                  <tr key={b.id} data-testid={`bank-row-${b.id}`} className="border-t border-slate-100">
                    <td className="p-3 font-semibold">{b.bank_name}</td>
                    <td className="p-3">{b.beneficiary}</td>
                    <td className="p-3 font-mono text-xs">{b.iban || b.account_number}</td>
                    <td className="p-3 font-mono text-xs">{b.swift}</td>
                    <td className="p-3">{b.currency}</td>
                    <td className="p-3 text-right">{b.min_deposit || 0}</td>
                    <td className="p-3"><button onClick={() => toggleBank(b)} data-testid={`bank-toggle-${b.id}`}><BooleanPill v={b.enabled} /></button></td>
                    <td className="p-3">
                      <div className="flex gap-1">
                        <Button size="sm" variant="outline" onClick={() => openBank(b)} data-testid={`bank-edit-${b.id}`}><Pencil size={14} /></Button>
                        <Button size="sm" variant="destructive" onClick={() => deleteBank(b.id)} data-testid={`bank-delete-${b.id}`}><Trash size={14} /></Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </TabsContent>

        <TabsContent value="crypto" className="mt-4">
          <div className="flex justify-end mb-3">
            <Button onClick={() => openWallet(null)} data-testid="add-wallet-btn"><Plus size={16} className="mr-1" />Add Crypto Wallet</Button>
          </div>
          <div className="bg-white rounded-xl border border-slate-200 overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 uppercase text-xs tracking-wider text-slate-600">
                <tr>
                  <th className="p-3 text-left">Coin</th><th className="p-3 text-left">Network</th>
                  <th className="p-3 text-left">Address</th><th className="p-3 text-left">Memo/Tag</th>
                  <th className="p-3 text-right">Min</th><th className="p-3">Status</th><th className="p-3">Actions</th>
                </tr>
              </thead>
              <tbody>
                {loading ? <tr><td colSpan="7" className="p-6 text-center text-slate-500">Loading…</td></tr>
                : wallets.length === 0 ? <tr><td colSpan="7" className="p-6 text-center text-slate-500">No crypto wallets configured yet.</td></tr>
                : wallets.map(w => (
                  <tr key={w.id} data-testid={`wallet-row-${w.id}`} className="border-t border-slate-100">
                    <td className="p-3 font-semibold">{w.coin}</td>
                    <td className="p-3">{w.network}</td>
                    <td className="p-3 font-mono text-xs break-all">{w.address}</td>
                    <td className="p-3 font-mono text-xs">{w.memo || '—'}</td>
                    <td className="p-3 text-right">{w.min_deposit || 0}</td>
                    <td className="p-3"><button onClick={() => toggleWallet(w)} data-testid={`wallet-toggle-${w.id}`}><BooleanPill v={w.enabled} /></button></td>
                    <td className="p-3">
                      <div className="flex gap-1">
                        <Button size="sm" variant="outline" onClick={() => openWallet(w)} data-testid={`wallet-edit-${w.id}`}><Pencil size={14} /></Button>
                        <Button size="sm" variant="destructive" onClick={() => deleteWallet(w.id)} data-testid={`wallet-delete-${w.id}`}><Trash size={14} /></Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </TabsContent>
      </Tabs>

      {/* Bank edit dialog */}
      <Dialog open={!!editingBank} onOpenChange={() => setEditingBank(null)}>
        <DialogContent className="max-w-2xl" data-testid="bank-edit-dialog">
          <DialogHeader><DialogTitle>{editingBank === 'new' ? 'Add Bank Account' : 'Edit Bank Account'}</DialogTitle></DialogHeader>
          <div className="grid grid-cols-2 gap-3 pt-2">
            {['bank_name','beneficiary','iban','swift','account_number','bank_address','beneficiary_address','reference','currency'].map(f => (
              <div key={f}>
                <Label className="capitalize">{f.replace(/_/g,' ')}</Label>
                <Input value={bankForm[f] || ''} onChange={e => setBankForm({...bankForm, [f]: e.target.value})} data-testid={`bank-input-${f}`} />
              </div>
            ))}
            <div>
              <Label>Minimum deposit</Label>
              <Input type="number" step="0.01" value={bankForm.min_deposit ?? 0} onChange={e=>setBankForm({...bankForm, min_deposit: e.target.value})} data-testid="bank-input-min" />
            </div>
            <div>
              <Label>Display order</Label>
              <Input type="number" value={bankForm.display_order ?? 0} onChange={e=>setBankForm({...bankForm, display_order: e.target.value})} />
            </div>
            <div className="col-span-2">
              <Label>Instructions</Label>
              <Textarea rows={3} value={bankForm.instructions || ''} onChange={e=>setBankForm({...bankForm, instructions: e.target.value})} data-testid="bank-input-instructions" />
            </div>
            <div className="col-span-2 flex items-center gap-2">
              <input type="checkbox" id="bank-enabled" checked={!!bankForm.enabled} onChange={e=>setBankForm({...bankForm, enabled: e.target.checked})} data-testid="bank-input-enabled" />
              <Label htmlFor="bank-enabled">Visible to clients on Deposit page</Label>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={()=>setEditingBank(null)}>Cancel</Button>
            <Button onClick={saveBank} disabled={saving} data-testid="bank-save">{saving ? 'Saving…' : 'Save'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Wallet edit dialog */}
      <Dialog open={!!editingWallet} onOpenChange={() => setEditingWallet(null)}>
        <DialogContent className="max-w-2xl" data-testid="wallet-edit-dialog">
          <DialogHeader><DialogTitle>{editingWallet === 'new' ? 'Add Crypto Wallet' : 'Edit Crypto Wallet'}</DialogTitle></DialogHeader>
          <div className="grid grid-cols-2 gap-3 pt-2">
            <div>
              <Label>Coin (BTC / ETH / USDT …)</Label>
              <Input value={walletForm.coin || ''} onChange={e=>setWalletForm({...walletForm, coin: e.target.value.toUpperCase()})} data-testid="wallet-input-coin" />
            </div>
            <div>
              <Label>Network (BTC / ERC20 / TRC20 / BEP20 …)</Label>
              <Input value={walletForm.network || ''} onChange={e=>setWalletForm({...walletForm, network: e.target.value})} data-testid="wallet-input-network" />
            </div>
            <div className="col-span-2">
              <Label>Wallet address</Label>
              <Input value={walletForm.address || ''} onChange={e=>setWalletForm({...walletForm, address: e.target.value})} className="font-mono" data-testid="wallet-input-address" />
            </div>
            <div>
              <Label>Memo / Tag (optional)</Label>
              <Input value={walletForm.memo || ''} onChange={e=>setWalletForm({...walletForm, memo: e.target.value})} />
            </div>
            <div>
              <Label>Minimum deposit</Label>
              <Input type="number" step="0.00000001" value={walletForm.min_deposit ?? 0} onChange={e=>setWalletForm({...walletForm, min_deposit: e.target.value})} />
            </div>
            <div className="col-span-2">
              <Label>Instructions</Label>
              <Textarea rows={3} value={walletForm.instructions || ''} onChange={e=>setWalletForm({...walletForm, instructions: e.target.value})} data-testid="wallet-input-instructions" />
            </div>
            <div className="col-span-2 flex items-center gap-2">
              <input type="checkbox" id="wallet-enabled" checked={!!walletForm.enabled} onChange={e=>setWalletForm({...walletForm, enabled: e.target.checked})} data-testid="wallet-input-enabled" />
              <Label htmlFor="wallet-enabled">Visible to clients on Deposit page</Label>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={()=>setEditingWallet(null)}>Cancel</Button>
            <Button onClick={saveWallet} disabled={saving} data-testid="wallet-save">{saving ? 'Saving…' : 'Save'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminPaymentSettings;
